import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiCall from '../../api';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { fetchPersonalData, updateBankData } from '../features/profile-settings/profile-settings.slice';
import { BankMethodSelector } from './bank/bank-method-selector.component';
import { BankOTPModal } from './BankOTPModal';
import { deriveBankMethodValue, buildManualBankPayload, isValidClearingNumber, isValidAccountNumber } from '../features/bank/bank.helpers';
import { BankMethodValue, BankFieldErrors } from '../features/bank/bank.types';
import { getCustomerDob } from '../utils/registration-validation';
import '../scss/bank-info-modal.style.scss';

interface BankInfoModalProps {
  show: boolean;
  onClose: () => void;
}

// Same Brite-first flow as profile settings / onboarding (BankMethodSelector +
// BankOTPModal, reading/writing through profile-settings.slice's
// fetchPersonalData/updateBankData) — previously this rendered the legacy
// manual-only <NewBankInformation> form here instead.
const BankInfoModal: React.FC<BankInfoModalProps> = ({ show, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((s: any) => s?.auth?.user);
  const personalData = useAppSelector((s: any) => s?.profileSettings?.personalData);

  const [bankValue, setBankValue] = useState<BankMethodValue>(() => deriveBankMethodValue(null));
  const [bankFieldErrors, setBankFieldErrors] = useState<BankFieldErrors>({});
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpField, setOtpField] = useState<string[]>(new Array(4).fill(''));
  const [otpCounter, setOtpCounter] = useState(300);
  const otpInputRefs = useRef<any[]>([]);

  // Load the current record whenever the modal opens, so the method selector
  // (and the "already linked" state inside BankMethodSelector's Brite panel)
  // reflects reality instead of defaulting blank every time.
  useEffect(() => {
    if (show && authUser?.userId) {
      dispatch(fetchPersonalData(authUser.userId) as any);
    }
  }, [show, authUser?.userId, dispatch]);

  useEffect(() => {
    if (personalData) {
      setBankValue(deriveBankMethodValue(personalData));
    }
  }, [personalData]);

  const refreshPersonalData = useCallback(() => {
    if (authUser?.userId) {
      dispatch(fetchPersonalData(authUser.userId) as any);
    }
  }, [authUser?.userId, dispatch]);

  const handleBriteCompleted = useCallback(() => {
    refreshPersonalData();
    onClose();
  }, [refreshPersonalData, onClose]);

  const generateOTPForBank = useCallback(async () => {
    try {
      const language = localStorage.getItem('i18nextLng') || 'en';
      const result = await apiCall.post('/settings/generate-otp', { type: 3, email: '', lang: language });
      if (result?.status === 200) {
        setOtpCounter(300);
        setOtpField(new Array(4).fill(''));
        setOtpModalOpen(true);
        toast.success(String(t('OTP_SENT_TO_EMAIL') || 'OTP sent to your email'));
      }
    } catch (error: any) {
      toast.error(String(t(error?.response?.data?.message) || 'Failed to generate OTP'));
    }
  }, [t]);

  const handleSaveChanges = useCallback(() => {
    const errors: BankFieldErrors = {};
    if (bankValue.method === 'other-swedish') {
      if (!bankValue.otherBankName?.trim()) errors.otherBankName = String(t('PLEASE_ENTER_BANK_NAME') || 'Please enter bank name');
      if (!isValidClearingNumber(bankValue.clearingNumber)) errors.clearingNumber = String(t('INVALID_CLEARING_FORMAT') || 'Invalid clearing number format');
      if (!isValidAccountNumber(bankValue.accountNumber)) errors.accountNumber = String(t('INVALID_ACCOUNT_FORMAT') || 'Invalid account number format');
    } else if (bankValue.method === 'foreign' || bankValue.method === 'revolut') {
      if (!bankValue.ibanNumber?.trim()) errors.ibanNumber = String(t('PLEASE_ENTER_IBAN') || 'Please enter IBAN');
      if (!bankValue.bicNumber?.trim()) errors.bicNumber = String(t('PLEASE_ENTER_BIC') || 'Please enter BIC');
    }
    setBankFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error(Object.values(errors)[0]);
      return;
    }
    generateOTPForBank();
  }, [bankValue, generateOTPForBank, t]);

  const submitBankWithOtp = useCallback(async (otp: string) => {
    try {
      const bankDataPayload = { ...buildManualBankPayload(bankValue), code: otp };
      await dispatch(updateBankData({ userId: authUser?.userId, bankData: bankDataPayload }) as any).unwrap();
      toast.success(String(t('BANK_DETAILS_UPDATED') || 'Your bank details have been added.'));
      setOtpModalOpen(false);
      setOtpField(new Array(4).fill(''));
      refreshPersonalData();
      onClose();
    } catch (err: any) {
      toast.error(String(t(err?.response?.data?.message) || 'Something went wrong'));
    }
  }, [bankValue, authUser?.userId, dispatch, refreshPersonalData, onClose, t]);

  const handleChangeOTP = useCallback(async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return;
    const newOtp = [...otpField];
    newOtp[index] = value.substring(value.length - 1);
    setOtpField(newOtp);
    if (value && index === otpField.length - 1 && newOtp.every((digit) => digit)) {
      await submitBankWithOtp(newOtp.join(''));
    }
  }, [otpField, submitBankWithOtp]);

  const resendOtp = useCallback(() => {
    setOtpField(new Array(4).fill(''));
    generateOTPForBank();
  }, [generateOTPForBank]);

  return (
    <Modal show={show} centered size="lg" onHide={onClose} className="bank-info-modal">
        <Modal.Header>
          <Modal.Title>
            {t('Please Add Your Bank Information')}
          </Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
            style={{ position: 'absolute', right: '16px', top: '16px' }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body style={{ overflow: 'auto' }}>
          <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#4a5568', marginBottom: '20px' }}>
            {t('It looks like you forgot to enter your bank details. To complete your setup and receive payments, you can securely enter them here to receive payments and complete your setup.')}
          </p>
          <BankMethodSelector
            value={bankValue}
            onChange={setBankValue}
            errors={bankFieldErrors}
            showRevolutMarketing={false}
            briteContext={{
              email: personalData?.email || '',
              firstName: personalData?.firstName || '',
              lastName: personalData?.lastName || '',
              dob: personalData?.dateOfBirth === '' ? getCustomerDob(personalData?.ssn) : getCustomerDob(personalData?.dateOfBirth),
              customerAddress: {
                city: personalData?.city,
                street: personalData?.address,
                postal_code: personalData?.zipCode,
                country_id: 'se',
              },
            }}
            onBriteCompleted={handleBriteCompleted}
            onBriteError={(message) => toast.error(message)}
            briteSurface="bank-info-modal"
          />
        </Modal.Body>
        {bankValue.method !== 'brite' && (
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('Close')}
            </Button>
            <Button type="submit" variant="primary" className="theme_btn" onClick={handleSaveChanges}>
              {t('Save Settings')}
            </Button>
          </Modal.Footer>
        )}

      <BankOTPModal
        OTPModal={otpModalOpen}
        setOTPModal={(v: boolean | number) => setOtpModalOpen(!!v)}
        resendOTP={resendOtp}
        OTPField={otpField}
        setOTPField={setOtpField}
        inputRefs={otpInputRefs}
        handleChangeOTP={handleChangeOTP}
        counter={otpCounter}
        setCounter={setOtpCounter}
        verifyEmail={null}
        newEmail={null}
        openInModal={false}
      />
    </Modal>
  );
};

export default BankInfoModal;
